// Node-level memory/skill binding UI (DEV-16815/16816) — the config-panel
// half of the node binding feature. Lets a specific node in a workflow
// graph see a memory/skill other nodes in the same workflow don't, without
// binding it to every agent that could ever run that node. Sits in the
// node inspector alongside the per-node-type config editor, not inside it
// — bindings are keyed by (workflowId, nodeKey), not part of the node's
// `config` JSON.
import { useMemo, useState } from 'react';

import { isMemoryStale, useMemoriesByIds, useMemoryList } from '../../../lib/agent-memory';
import { useSkillList } from '../../../lib/agent-skill';
import {
  useAddWorkflowNodeMemoryBinding,
  useAddWorkflowNodeSkillBinding,
  useRemoveWorkflowNodeMemoryBinding,
  useRemoveWorkflowNodeSkillBinding,
  useWorkflowNodeMemoryBindings,
  useWorkflowNodeSkillBindings,
} from '../../../lib/workflow-node-binding';

export function NodeBindingSection({
  workflowId,
  nodeKey,
  readOnly,
  /**
   * False while `nodeKey` hasn't been saved into the workflow's current
   * version yet (a freshly-added, still-unsaved node). The binding API
   * 404s for a nodeKey that isn't in `currentVersion.nodes`, so the list
   * queries stay disabled and the section explains why instead of
   * surfacing that 404 as a query error.
   */
  persisted,
}: {
  workflowId: string;
  nodeKey: string;
  readOnly: boolean;
  persisted: boolean;
}) {
  return (
    <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <span className="field-label">Node-scoped memory &amp; skills</span>
      <span className="field-help">
        Bound here, this node (and only this node) can see these — separate from any agent-level
        binding.
      </span>
      {!persisted ? (
        <span className="t-xs t-muted">Save this node before binding memory or skills.</span>
      ) : (
        <>
          <MemoryBindingList workflowId={workflowId} nodeKey={nodeKey} readOnly={readOnly} />
          <SkillBindingList workflowId={workflowId} nodeKey={nodeKey} readOnly={readOnly} />
          <NodeAlsoSees />
        </>
      )}
    </div>
  );
}

/**
 * "What this node also sees" (DEV-17005). A node's effective resolved set is
 * `appliesToAll ∪ bound-to-this-node` — the bindings above plus any org-wide
 * (appliesToAll) memories/skills. Agent collections are agent groups and do
 * NOT apply to nodes, so they're never part of a node's set.
 */
function NodeAlsoSees() {
  const memoriesQuery = useMemoryList({ status: 'APPROVED' });
  const skillsQuery = useSkillList();
  // Expired memories are excluded by the runtime resolver — don't count them.
  const orgMemories = (memoriesQuery.data ?? []).filter(
    (m) => m.appliesToAll && !isMemoryStale(m),
  ).length;
  const orgSkills = (skillsQuery.data ?? []).filter((s) => s.appliesToAll).length;
  return (
    <>
      {orgMemories > 0 || orgSkills > 0 ? (
        <span className="t-xs t-muted">
          Beyond the bindings above, this node also resolves {orgMemories} org-wide memor
          {orgMemories === 1 ? 'y' : 'ies'} and {orgSkills} org-wide skill
          {orgSkills === 1 ? '' : 's'} (appliesToAll).
        </span>
      ) : null}
      <span className="t-xs t-muted">Agent collections do not apply to nodes.</span>
    </>
  );
}

interface IBindingListItem {
  id: string;
  label: string;
}

/**
 * Shared list/select/add/remove UI for one bindable entity kind. Memory and
 * skill bindings differ only in their data source and label formatting —
 * both `MemoryBindingList` and `SkillBindingList` below reduce to fetching
 * their own query/mutations and mapping items into `IBindingListItem`
 * before handing off to this renderer, instead of each re-implementing the
 * list/picker markup.
 */
function BindingList({
  title,
  boundItems,
  availableItems,
  selected,
  onSelectedChange,
  onAdd,
  onRemove,
  addPending,
  removePending,
  addErrorMessage,
  readOnly,
  emptyPickerLabel,
  pickerPlaceholder,
}: {
  title: string;
  boundItems: IBindingListItem[];
  availableItems: IBindingListItem[];
  selected: string;
  onSelectedChange: (id: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  addPending: boolean;
  removePending: boolean;
  addErrorMessage: string | null;
  readOnly: boolean;
  emptyPickerLabel: string;
  pickerPlaceholder: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span className="t-xs t-muted">
        {title} ({boundItems.length})
      </span>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {boundItems.map((item) => (
          <li
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-1) 0',
            }}
          >
            <span className="t-xs" style={{ flex: 1 }}>
              {item.label}
            </span>
            {!readOnly ? (
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                disabled={removePending}
                onClick={() => onRemove(item.id)}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {!readOnly ? (
        <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
          <select
            className="input"
            value={selected}
            onChange={(e) => onSelectedChange(e.target.value)}
            disabled={availableItems.length === 0}
          >
            <option value="">
              {availableItems.length === 0 ? emptyPickerLabel : pickerPlaceholder}
            </option>
            {availableItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            disabled={!selected || addPending}
            onClick={onAdd}
          >
            Add
          </button>
        </div>
      ) : null}
      {addErrorMessage ? (
        <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
          {addErrorMessage}
        </span>
      ) : null}
    </div>
  );
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function MemoryBindingList({
  workflowId,
  nodeKey,
  readOnly,
}: {
  workflowId: string;
  nodeKey: string;
  readOnly: boolean;
}) {
  const bindingsQuery = useWorkflowNodeMemoryBindings(workflowId, nodeKey);
  const memoriesQuery = useMemoryList({ status: 'APPROVED' });
  const add = useAddWorkflowNodeMemoryBinding(workflowId, nodeKey);
  const remove = useRemoveWorkflowNodeMemoryBinding(workflowId, nodeKey);
  const [selected, setSelected] = useState('');

  const boundIds = useMemo(
    () => new Set((bindingsQuery.data ?? []).map((b) => b.memoryId)),
    [bindingsQuery.data],
  );
  // The picker only ever offers APPROVED memories, but a binding can point at
  // a memory in any status (e.g. still PENDING review) — fall back to a
  // by-id lookup so a bound-but-not-approved memory still shows its content
  // instead of its raw id.
  const boundMemoriesById = useMemoriesByIds([...boundIds]);
  const memoriesById = useMemo(
    () => new Map((memoriesQuery.data ?? []).map((m) => [m.id, m])),
    [memoriesQuery.data],
  );
  const boundItems = useMemo(
    () =>
      [...boundIds].map((id) => {
        const m = memoriesById.get(id) ?? boundMemoriesById.get(id);
        return { id, label: m ? truncate(m.content, 60) : id };
      }),
    [boundIds, memoriesById, boundMemoriesById],
  );
  const availableItems = useMemo(
    () =>
      (memoriesQuery.data ?? [])
        .filter((m) => !boundIds.has(m.id))
        .map((m) => ({ id: m.id, label: truncate(m.content, 60) })),
    [memoriesQuery.data, boundIds],
  );

  return (
    <BindingList
      title="Memory"
      boundItems={boundItems}
      availableItems={availableItems}
      selected={selected}
      onSelectedChange={setSelected}
      onAdd={() => {
        add.mutate(selected);
        setSelected('');
      }}
      onRemove={(id) => remove.mutate(id)}
      addPending={add.isPending}
      removePending={remove.isPending}
      addErrorMessage={add.error ? (add.error as Error).message : null}
      readOnly={readOnly}
      emptyPickerLabel="No more memories to bind"
      pickerPlaceholder="Bind a memory…"
    />
  );
}

function SkillBindingList({
  workflowId,
  nodeKey,
  readOnly,
}: {
  workflowId: string;
  nodeKey: string;
  readOnly: boolean;
}) {
  const bindingsQuery = useWorkflowNodeSkillBindings(workflowId, nodeKey);
  const skillsQuery = useSkillList();
  const add = useAddWorkflowNodeSkillBinding(workflowId, nodeKey);
  const remove = useRemoveWorkflowNodeSkillBinding(workflowId, nodeKey);
  const [selected, setSelected] = useState('');

  const boundIds = useMemo(
    () => new Set((bindingsQuery.data ?? []).map((b) => b.skillId)),
    [bindingsQuery.data],
  );
  const skillsById = useMemo(
    () => new Map((skillsQuery.data ?? []).map((s) => [s.id, s])),
    [skillsQuery.data],
  );
  const boundItems = useMemo(
    () =>
      [...boundIds].map((id) => {
        const s = skillsById.get(id);
        return { id, label: s?.name ?? id };
      }),
    [boundIds, skillsById],
  );
  const availableItems = useMemo(
    () =>
      (skillsQuery.data ?? [])
        .filter((s) => !boundIds.has(s.id))
        .map((s) => ({ id: s.id, label: s.name })),
    [skillsQuery.data, boundIds],
  );

  return (
    <BindingList
      title="Skills"
      boundItems={boundItems}
      availableItems={availableItems}
      selected={selected}
      onSelectedChange={setSelected}
      onAdd={() => {
        add.mutate(selected);
        setSelected('');
      }}
      onRemove={(id) => remove.mutate(id)}
      addPending={add.isPending}
      removePending={remove.isPending}
      addErrorMessage={add.error ? (add.error as Error).message : null}
      readOnly={readOnly}
      emptyPickerLabel="No more skills to bind"
      pickerPlaceholder="Bind a skill…"
    />
  );
}
